/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import { dataPixels } from '@web-stories-wp/units';
/**
 * Internal dependencies
 */
import {
  calculateFitTextFontSize,
  calculateTextHeight,
} from '../../utils/textMeasurements';
import {
  getBoundedTypographyScale,
  scaleInlineFontSizes,
} from './utils/mixedTypography';
import { scaleTextListStyle } from './utils/listStyle';

function updateForResizeEvent({
  element,
  direction,
  newWidth,
  newHeight,
  dataToEditorX,
  dataToEditorY,
  isProportional = false,
  shouldUpdateContent = true,
}) {
  const isResizingWidth = direction[0] !== 0;
  const isResizingHeight = direction[1] !== 0;
  const hasContent = typeof element.content === 'string';
  const typographyElement = hasContent ? element : { ...element, content: '' };

  // Vertical or diagonal resizing w/keep ratio.
  if (isResizingHeight) {
    if (
      isProportional &&
      isResizingWidth &&
      element.width &&
      Number.isFinite(newWidth)
    ) {
      const proportionalScale = newWidth / element.width;
      const scale = getBoundedTypographyScale(
        typographyElement,
        proportionalScale
      );
      return {
        fontSize: element.fontSize * scale,
        ...(shouldUpdateContent && hasContent
          ? { content: scaleInlineFontSizes(element.content, scale) }
          : {}),
        ...(element.listStyle
          ? {
              listStyle: scaleTextListStyle(
                element.listStyle,
                scale,
                element.fontSize
              ),
            }
          : null),
      };
    }

    const { fontSize, marginOffsetTop } = calculateFitTextFontSize({
      element,
      width: newWidth || element.width,
      height: newHeight,
      dataToEditorX,
      dataToEditorY,
    });
    const fontSizeInDataUnits = dataPixels(fontSize);
    const scale = getBoundedTypographyScale(
      typographyElement,
      element.fontSize ? fontSizeInDataUnits / element.fontSize : 1
    );
    const boundedFontSize = element.fontSize
      ? element.fontSize * scale
      : fontSizeInDataUnits;

    return {
      fontSize: boundedFontSize,
      ...(shouldUpdateContent && hasContent
        ? { content: scaleInlineFontSizes(element.content, scale) }
        : {}),
      marginOffset: marginOffsetTop,
      marginOffsetTop,
      ...(element.listStyle
        ? {
            listStyle: scaleTextListStyle(
              element.listStyle,
              scale,
              element.fontSize
            ),
          }
        : null),
    };
  }

  // Width-only resize: recalc height.
  if (isResizingWidth) {
    return {
      height: dataPixels(
        calculateTextHeight(element, newWidth, {
          dataToEditorX,
          dataToEditorY,
        })
      ),
    };
  }

  return null;
}

export default updateForResizeEvent;
